import type { IntakeDescriptor } from "@/features/intake/intake-buckets"
import type { OiaDocumentRole } from "../types"

/**
 * The three things an Order-in-Appeal is written from.
 *
 *   order   — what was decided below. The mandatory anchor of every appeal:
 *             the run refuses a file with no impugned order, so this is the
 *             bucket whose emptiness must be said BEFORE the run is paid for.
 *   appeal  — what the appellant filed against it: the form and the grounds,
 *             in appellate format (APL-01 + memo) or the department's (APL-03
 *             + review order), with the statement of facts that travels with
 *             either.
 *   other   — everything else on the record. Never required, never wrong.
 *
 * The statement of facts sits with the APPEAL, not the order, deliberately. It
 * recites the order but cannot stand in for it: a bundle holding only a
 * statement of facts would tick "order" here and then be refused at intake
 * after the extraction had been run and paid for.
 *
 * Every docextract sub-type is placed by hand except "other", which says
 * nothing and so defers to the role. A sub-type this table has never heard of
 * falls to the role and then to `other` — the safe side, because `other` is
 * the one bucket that can never hide a missing document.
 */
export type OiaIntakeBucketId = "order" | "appeal" | "other"

export const OIA_INTAKE: IntakeDescriptor<OiaIntakeBucketId, OiaDocumentRole> = {
  buckets: [
    {
      id: "order",
      label: "Order under appeal",
      hint: "The order being appealed — the speaking order, or its DRC-07 / RFD-06 print.",
      required: true,
    },
    {
      id: "appeal",
      label: "Appeal & grounds",
      hint: "What was filed against it — APL-01 or APL-03, the grounds, the statement of facts, a review order.",
      required: true,
    },
    {
      id: "other",
      label: "Other documents",
      hint: "Anything else on record — the notice and reply below, hearing records, payments, exhibits.",
      required: false,
    },
  ],
  fallback: "other",
  bySubType: {
    // The order under appeal, in every form it is issued or printed.
    oio: "order",
    state_proceedings_order: "order",
    drc07_attachment: "order",
    rfd06_speaking_order: "order",
    reg_cancellation_order: "order",
    reg_revocation_rejection_order: "order",
    s161_rectification_order: "order",
    penalty_order: "order",
    detention_order: "order",
    drc07_summary: "order",
    drc08_rectification_summary: "order",
    rfd06_portal_form: "order",
    // The appeal as filed.
    apl01_portal_print: "appeal",
    apl01_annexure: "appeal",
    apl03_portal_print: "appeal",
    apl03_annexure: "appeal",
    appeal_memo: "appeal",
    statement_of_facts: "appeal",
    grounds_of_appeal: "appeal",
    review_order: "appeal",
    condonation_petition: "appeal",
    additional_submission: "appeal",
    // Everything else on record.
    apl02_acknowledgement: "other",
    affidavit: "other",
    authorisation_letter: "other",
    covering_letter: "other",
    paperbook_index: "other",
    department_parawise_remarks: "other",
    appellate_office_correspondence: "other",
    ph_record: "other",
    ph_notice: "other",
    drc03_payment: "other",
    predeposit_ledger_or_challan: "other",
    scn: "other",
    drc01_summary: "other",
    drc01a_intimation: "other",
    reply_to_scn: "other",
    rfd08_refund_scn: "other",
    rfd09_refund_reply: "other",
    rfd01_refund_application: "other",
    oia: "other",
    apl04_summary: "other",
    judgment_copy: "other",
    ca_certificate: "other",
    financial_statements: "other",
    invoices: "other",
    returns_or_ledgers: "other",
    correspondence: "other",
    portal_screenshot: "other",
    exhibit_other: "other",
  },
  byRole: {
    impugned_order: "order",
    impugned_order_summary: "order",
    underlying_order: "order",
    appeal_form: "appeal",
    appeal_memo: "appeal",
    statement_of_facts: "appeal",
    review_order: "appeal",
    department_appeal_form: "appeal",
    additional_submission: "appeal",
    condonation_petition: "appeal",
  },
  // An earlier order tendered as an exhibit IS an order, and must not tick
  // the box for the order under appeal.
  overrulingRoles: ["exhibit", "prior_appellate_order", "relied_judgment_copy"],
  hintFor: {
    order: "impugned_order",
    appeal: "appeal_memo",
    other: "other",
  },
}
